import { registerLocaleData } from "@angular/common";
import localeGermany from "@angular/common/locales/de";
import localeEnGb from "@angular/common/locales/en-GB";
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from "@angular/router";
import { IconButtonComponent } from "@claas/claas-ui-components";
import { HeaderComponent } from "@claas/claas-ui-components/navigation/header";
import {
  NavigationComponent,
  NavigationItem,
} from "@claas/claas-ui-components/navigation/navigation";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { filter, map } from "rxjs";
import { navigationStore } from "./domains/coremedia/navigation/data/navigation.store";
import { LoadingProgressBarComponent } from "./domains/pia/shared/components/loading-progress-bar/loading-progress-bar";
import { ScrollToTop } from "./domains/pia/shared/components/scroll-to-top/scroll-to-top";
import { ScrollAnchorDirective } from "./domains/pia/shared/directives/scroll-anchor";
import { UserOverlayComponent } from "./domains/user/feature/user-overlay/user-overlay";
import { LocaleEffectsService } from "./shared/data/locale-effects.service";
import { RuntimeConfigService } from "./shared/runtime-config.service";

registerLocaleData(localeEnGb);
registerLocaleData(localeGermany);

const LANGUAGE_PROVIDERS = [
  { provide: localeEnGb, useValue: "en-GB" },
  { provide: localeGermany, useValue: "de" },
];

@Component({
  selector: "app-root",
  templateUrl: "./app.html",
  styleUrls: ["./app.scss"],
  imports: [
    TranslateModule,
    NavigationComponent,
    HeaderComponent,
    IconButtonComponent,
    RouterLink,
    RouterOutlet,
    ScrollAnchorDirective,
    ScrollToTop,
    LoadingProgressBarComponent,
    UserOverlayComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [...LANGUAGE_PROVIDERS],
})
export class App {
  private readonly navigationStore = inject(navigationStore);
  // Inject LocaleEffectsService at root to activate translate + nav reload effects
  private readonly localeEffects = inject(LocaleEffectsService);
  private readonly runtimeConfig = inject(RuntimeConfigService);
  private readonly router = inject(Router);

  private readonly translate = inject(TranslateService);
  // Recompute the menu (PIA item label) whenever the active language changes.
  private readonly langChange = toSignal(this.translate.onLangChange, {
    initialValue: null,
  });
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );
  // PIA's global styles are scoped to the `.pia` content wrapper, applied only
  // while a /pia route is active so they never affect the poff shell.
  public readonly isPiaRoute = computed(() =>
    this.currentUrl().startsWith("/pia"),
  );
  public readonly isCmsRoute = computed(() =>
    /^\/cms(?:\/|[?#]|$)/.test(this.currentUrl()),
  );
  // True as soon as the navigation has finished loading once, successfully or
  // not. Later loads retain the existing menu during country/language changes.
  private readonly initialLoadDone = signal(false);
  public readonly menuItems = computed<NavigationItem[] | undefined>(() => {
    const status = this.navigationStore.status();
    // Hide menu items only during the first load. The template must keep the
    // shell mounted so the Okta callback can run before navigation loads and
    // the overlay host survives later reloads.
    if (
      !this.initialLoadDone() &&
      (status === "loading" || status === "idle")
    ) {
      return undefined;
    }
    this.langChange();
    const piaItem: NavigationItem = {
      label: this.translate.instant("features.product-overview.headline"),
      route: "pia",
      activeRoutes: ["pia"],
      iconId: "648467",
      iconNamespace: "lib",
    };
    return [...this.navigationStore.items(), piaItem];
  });
  expanded = signal(true);

  protected readonly navigateToDashboard = (): void => {
    void this.router.navigate(["/"]);
  };

  constructor() {
    effect(() => {
      const status = this.navigationStore.status();
      if (status === "ready" || status === "error") {
        this.initialLoadDone.set(true);
      }
    });

    effect(() => {
      const status = this.navigationStore.status();
      if (status === "ready" || status === "error") {
        this.initialLoadDone.set(true);
      }
    });
  }

  protected onLogoClicked(event: MouseEvent | KeyboardEvent): void {
    if (event instanceof KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
    }
    this.navigateToDashboard();
  }

  protected get softwareStatusLabel(): string | undefined {
    return this.runtimeConfig.softwareStatusLabel;
  }
}
